import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';
import formatInstantInTimeZone, { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/formatInstantInTimeZone';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import JsonView from '../../../components/JsonView';
import { assistantFeedbackAnchorPath } from '../../../paths';

import AttachmentDownloadLink from './AttachmentDownloadLink';
import {
  AUDIT_PARAM_COLOR_CLASSES,
  AUDIT_PARAM_KEYS,
  buildToolCallArgsIndex,
  buildTurnSegments,
  COLLAPSED_TOOL_RESULT_KEYS,
  DEFAULT_ROLE_ACCENT,
  ELLIPSIZED_STRING_KEYS,
  extractTextBlocks,
  formatDurationMs,
  formatTurnDelta,
  PROMINENT_CONTENT_COLOR_CLASSES,
  PROSE_COLOR_CLASSES,
  readObservationDocuments,
  readPayloadKind,
  readToolCallId,
  readToolResultPayload,
  ROLE_ACCENT,
  formatToolParams,
  type FeedbackRow,
  type IRoleAccent,
  type ITurnGroup,
  type MessageRow,
} from './transcriptModel';

/**
 * One label/value cell rendered as a pair of fixed-width sub-cells:
 * label right-aligned in `w-[3ch]`, value right-aligned in `w-[7ch]`.
 * Used in both MessageCard's metadata strip and the parallel-group
 * header — sharing the helper guarantees the columns align across the
 * two surfaces (every chip's value-right-edge lands at the same
 * screen-X regardless of row type). Pass empty strings to render an
 * invisible placeholder cell that still consumes column width — keeps
 * `Δ`/`lat` aligned with their per-row siblings when the chip set
 * differs between contexts.
 */
const renderMetricChip = (label: string, value: string, valueClass = ''): React.ReactNode => (
  // `shrink-0` on the chip wrapper AND on each sub-span prevents flex
  // from collapsing empty-content cells (e.g. the placeholder seq /
  // 🧠 slots on the parallel-group header). Without it, empty cells
  // shrink to zero and the populated chips drift left of their per-row
  // siblings, breaking column alignment.
  <span className="flex shrink-0 items-baseline gap-1.5">
    <span className="text-fg-quaternary w-[3ch] shrink-0 text-right">{label}</span>
    <span className={cx('w-[7ch] shrink-0 text-right', valueClass)}>{value}</span>
  </span>
);

interface IParallelMetadataProps {
  /** ms since epoch — earliest `startedAt` across the parallel siblings. */
  startedAtMs: number | null;
  /** Max `latencyMs` across the siblings (the slowest tool gates the block). */
  maxLatencyMs: number | null;
  /** ms since epoch of the turn's earliest `startedAt`, for the Δ chip. */
  turnStartedAtMs: number | null;
}

/**
 * Wraps `renderMetricChip`'s output in a per-row metadata container
 * matching MessageCard's strip. Used by the parallel-group header to
 * align its Δ / lat chips with the per-row chips below.
 */
const ParallelMetadata = ({
  startedAtMs,
  maxLatencyMs,
  turnStartedAtMs,
}: IParallelMetadataProps): React.ReactElement => (
  <span className="text-fg-tertiary flex flex-1 justify-end gap-3 font-mono normal-case tabular-nums">
    {renderMetricChip('', '')}
    <span title="Delta from turn start: earliest startedAt across the parallel siblings (≈ when the assistant dispatched the parallel tool batch).">
      {renderMetricChip('Δ', formatTurnDelta(startedAtMs !== null ? new Date(startedAtMs) : null, turnStartedAtMs))}
    </span>
    {renderMetricChip('', '')}
    {renderMetricChip('', '')}
    <span title="Group latency: max(latencyMs) across the parallel siblings. The slowest tool gates the next round, so this is the wall-clock duration of the parallel block.">
      {renderMetricChip('lat', formatDurationMs(maxLatencyMs), 'text-fg-primary')}
    </span>
  </span>
);

interface IToolExpansionSectionProps {
  /** Sub-section label rendered above the JSON (e.g. `toolCall`, `toolResult`). */
  label: string;
  children: React.ReactNode;
}

/**
 * A labeled sub-section inside an expanded toolResult card. Splits the
 * expansion into the originating `toolCall` and its `toolResult` so an
 * operator can read the call's arguments (with the audit params surfaced)
 * alongside the result.
 */
const ToolExpansionSection = ({ label, children }: IToolExpansionSectionProps) => (
  <section>
    <div className="text-fg-quaternary mb-1 text-[9px] uppercase tracking-wider">{label}</div>
    {children}
  </section>
);

interface ICollapsibleMessageCardProps {
  message: MessageRow;
  accent: IRoleAccent;
  metadata: React.ReactNode;
  /** Extra header bits sitting next to the role label (toolName, failed badge, kind discriminator, etc). */
  headerExtras?: React.ReactNode;
  /**
   * Optional one-line preview of the message body, rendered inline
   * after the role label and ellipsized to fit the header row. Lets an
   * operator scan a transcript without expanding every card. Full text
   * is still in the JSON below on expand. Accepts `ReactNode` so
   * callers can render styled placeholders (e.g. italic `Thinking…` on
   * a tool-use-only assistant round).
   */
  headerPreview?: React.ReactNode;
  /**
   * Custom expanded body, rendered in place of the default whole-payload
   * `JsonView`. Lets a role split the expansion into labeled sub-sections
   * (e.g. a toolResult's `toolCall` + `toolResult`). Only constructed by
   * the caller, but only mounted when the card is open.
   */
  expandedBody?: React.ReactNode;
  /**
   * Per-key value colors forwarded to the default-body `JsonView` (e.g. a
   * user message's `content` bold yellow). When omitted, the default body
   * falls back to the bold bright-white prose treatment. Ignored when
   * `expandedBody` is set.
   */
  stringKeyClasses?: ReadonlyMap<string, string>;
  /**
   * Content rendered below the header regardless of collapse state — i.e.
   * outside the collapse contract the rest of the card follows. Use for
   * always-actionable affordances that shouldn't require expanding the card
   * (e.g. attachment download links on an `attachment` observation). Sits
   * between the header button and the `errorMessage` line.
   */
  alwaysVisibleBody?: React.ReactNode;
}

/**
 * Shared collapsible card. Five roles use it (toolResult, assistant,
 * user, system_instruction, system_observation) — the JSON payload is
 * hidden by default and expands on click. Header shows the chevron,
 * role label, optional extras, an optional ellipsized preview of the
 * message body, and the right-aligned metadata strip.
 */
const CollapsibleMessageCard = ({
  message,
  accent,
  metadata,
  headerExtras,
  headerPreview,
  expandedBody,
  stringKeyClasses,
  alwaysVisibleBody,
}: ICollapsibleMessageCardProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const toggle = () => setExpanded((v) => !v);
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <button
        type="button"
        onClick={toggle}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
        aria-expanded={expanded}
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className={cx('shrink-0', accent.label)}>{message.role}</span>
        {headerExtras !== undefined && <span className="shrink-0">{headerExtras}</span>}
        {headerPreview !== undefined && (
          <span className={cx('min-w-0 flex-1 truncate normal-case', accent.prose)}>{headerPreview}</span>
        )}
        <span className="text-fg-tertiary ml-auto flex shrink-0 gap-2">{metadata}</span>
      </button>
      {expanded && (
        <div className="whitespace-pre-wrap pl-1 pt-2 font-mono text-[11px] leading-snug">
          {expandedBody ?? (
            <JsonView
              value={message.payload}
              collapseArrayKeys={message.role === 'toolResult' ? COLLAPSED_TOOL_RESULT_KEYS : undefined}
              stringKeyClasses={stringKeyClasses ?? PROMINENT_CONTENT_COLOR_CLASSES}
              ellipsizeStringKeys={ELLIPSIZED_STRING_KEYS}
            />
          )}
        </div>
      )}
      {alwaysVisibleBody !== undefined && <div className="pl-1 pt-1.5">{alwaysVisibleBody}</div>}
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">errorMessage: {message.errorMessage}</div>
      )}
    </article>
  );
};

interface IMessageCardProps {
  message: MessageRow;
  /** ms since epoch of the turn's earliest `startedAt`, for the Δ chip. */
  turnStartedAtMs: number | null;
  /**
   * Map of `toolUseId → input` built once per turn. The toolResult
   * branch reads this to render a one-line param summary next to the
   * tool name. Non-toolResult rows ignore it.
   */
  toolCallArgs: Map<string, unknown>;
}

const MessageCard = ({ message, turnStartedAtMs, toolCallArgs }: IMessageCardProps) => {
  const accent = ROLE_ACCENT[message.role] ?? DEFAULT_ROLE_ACCENT;
  // Each cell is two stacked fixed-width sub-cells: label
  // (right-aligned, `w-[3ch]`) and value (right-aligned, `w-[7ch]`)
  // with a tight gap between. Labels land at the same screen-X across
  // every row (right edge of the label sub-cell), values' right edges
  // align too, and the inter-pair gap is fixed regardless of value
  // length. Empty cells still hold their width so alignment doesn't
  // collapse when a chip is absent. `normal-case` breaks out of the
  // parent header's `uppercase` so digits stay readable. `modelId` is
  // intentionally dropped from per-row chips — it's the same
  // hardcoded value on every assistant row in a session; the JSON
  // expansion still has it.
  const hasLatency = message.latencyMs !== null && message.latencyMs !== undefined;
  const hasThinking = message.thinkingMs !== null && message.thinkingMs !== undefined;
  const hasTtft = message.ttftMs !== null && message.ttftMs !== undefined;
  const metadata = (
    <span className="flex items-center gap-3 font-mono normal-case tabular-nums">
      <span title="Per-thread sequence: monotonic counter assigned at append time. Stable across rehydration; used as the keyset cursor for incremental loads.">
        {renderMetricChip('seq', String(message.sequence))}
      </span>
      <span title="Delta from turn start: orchestrator wall-clock between this row's startedAt and the first row of the turn. The first row of each turn renders +0ms.">
        {renderMetricChip('Δ', formatTurnDelta(message.startedAt ?? null, turnStartedAtMs))}
      </span>
      <span title="Wall-clock time the model spent inside thinking blocks during this round. Summed across interleaved blocks (Anthropic extended thinking). NULL on tool-result, synthetic, and non-thinking rows.">
        {renderMetricChip(hasThinking ? '🧠' : '', hasThinking ? formatDurationMs(message.thinkingMs ?? null) : '')}
      </span>
      <span title="Time-to-first-token: orchestrator wall-clock from round start to the first user-facing text token. Excludes thinking and tool-call time — the user-perceived latency.">
        {renderMetricChip(hasTtft ? 'ttft' : '', hasTtft ? formatDurationMs(message.ttftMs ?? null) : '')}
      </span>
      <span title="Latency: full wall-clock duration of this round (completedAt − startedAt). For tool-result rows, the tool execution time. Includes thinking + post-text streaming for assistant rounds.">
        {renderMetricChip(
          hasLatency ? 'lat' : '',
          hasLatency ? formatDurationMs(message.latencyMs ?? null) : '',
          'text-fg-primary'
        )}
      </span>
    </span>
  );

  if (message.role === 'toolResult') {
    const toolMeta = readToolResultPayload(message.payload);
    const toolCallId = readToolCallId(message.payload);
    const toolCallArguments = toolCallId !== null ? toolCallArgs.get(toolCallId) : undefined;
    const hasToolCall = toolCallArguments !== undefined;
    const paramsPreview = formatToolParams(toolCallArguments, AUDIT_PARAM_KEYS);
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerPreview={paramsPreview.length > 0 ? paramsPreview : undefined}
        headerExtras={
          <>
            {toolMeta.toolName && <span className="font-bold normal-case text-fuchsia-400">{toolMeta.toolName}</span>}
            {toolMeta.isError && (
              <span className="text-fg-error-primary border-fg-error-primary inline-flex items-center border px-1 py-px text-[9px] uppercase tracking-wider">
                failed
              </span>
            )}
          </>
        }
        expandedBody={
          hasToolCall ? (
            <div className="flex flex-col gap-3">
              <ToolExpansionSection label="toolCall">
                <JsonView value={toolCallArguments} stringKeyClasses={AUDIT_PARAM_COLOR_CLASSES} />
              </ToolExpansionSection>
              <ToolExpansionSection label="toolResult">
                <JsonView
                  value={message.payload}
                  collapseArrayKeys={COLLAPSED_TOOL_RESULT_KEYS}
                  stringKeyClasses={PROMINENT_CONTENT_COLOR_CLASSES}
                />
              </ToolExpansionSection>
            </div>
          ) : undefined
        }
      />
    );
  }

  if (message.role === 'assistant' || message.role === 'user') {
    const text = extractTextBlocks(message.payload).join(' ');
    // Tool-use-only assistant rounds emit no user-facing text — the
    // model decided to call tools first. Surface a dimmed italic
    // placeholder so the row reads as "no message yet, still working"
    // rather than as an empty line the operator might miss.
    const preview: React.ReactNode =
      text.length > 0 ? (
        text
      ) : message.role === 'assistant' ? (
        <span className="text-fg-tertiary italic">Thinking…</span>
      ) : undefined;
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerPreview={preview}
        stringKeyClasses={PROSE_COLOR_CLASSES}
      />
    );
  }

  if (message.role === 'system_instruction' || message.role === 'system_observation') {
    const kind = readPayloadKind(message.payload);
    // Attachment observations carry the uploaded documents; surface each as
    // an always-visible download link so an operator can open the file
    // without expanding the JSON.
    const documents =
      message.role === 'system_observation' && kind === 'attachment' ? readObservationDocuments(message.payload) : [];
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerExtras={kind && <span className="text-fg-secondary normal-case">{kind}</span>}
        alwaysVisibleBody={
          documents.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {documents.map((doc) => (
                <AttachmentDownloadLink key={doc.id} documentId={doc.id} name={doc.name} />
              ))}
            </div>
          ) : undefined
        }
      />
    );
  }

  // Fallthrough — unknown roles render uncollapsed (their shape is
  // unknown, so the JSON IS the signal). Add a dedicated branch above
  // when a new role needs custom rendering.
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <header className="text-fg-tertiary flex gap-2 font-mono text-[10px] uppercase">
        <span className={accent.label}>{message.role}</span>
        {metadata}
      </header>
      <div className="whitespace-pre-wrap pl-1 pt-1 font-mono text-[11px] leading-snug">
        <JsonView
          value={message.payload}
          stringKeyClasses={PROMINENT_CONTENT_COLOR_CLASSES}
          ellipsizeStringKeys={ELLIPSIZED_STRING_KEYS}
        />
      </div>
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">errorMessage: {message.errorMessage}</div>
      )}
    </article>
  );
};

interface ITurnGroupProps {
  group: ITurnGroup;
  /** Internal `/issue` feedback targeting this turn (empty when none). */
  feedback: FeedbackRow[];
  /** When true (deep-linked turn), scroll into view + ring the section. */
  highlight: boolean;
}

/**
 * Render one turn group: a sticky header summarizing the turn (start
 * timestamp, turnId, message count, round count, tool-call count,
 * duration) followed by the group's rows indented under a left border
 * so the boundary between turns is visible. `MessageCard` receives the
 * group's `startedAtMs` so it can render a Δ-from-turn-start chip.
 *
 * Start-timestamp source: the group's `startedAtMs` (orchestrator-
 * observed turn start). Falls back to the first row's `createdAt` for
 * legacy rows that predate the timing columns — keeps the chip
 * meaningful across the data migration boundary.
 *
 * Parallel tool calls are bracketed: contiguous `toolResult` runs of
 * 2+ get a `⇄ N parallel` header so an operator can tell at a glance
 * that those rows dispatched concurrently rather than serially (the
 * Δ-from-turn-start chips also reveal this on close inspection —
 * parallel siblings share a Δ — but the explicit bracket makes it
 * scannable).
 */
export const TurnGroup = ({ group, feedback, highlight }: ITurnGroupProps) => {
  const headerStartedAtMs = group.startedAtMs ?? new Date(group.messages[0].createdAt).getTime();
  const headerTimestamp = formatInstantInTimeZone(new Date(headerStartedAtMs), PACIFIC_TIME_ZONE);
  const segments = buildTurnSegments(group.messages);
  const toolCallArgs = React.useMemo(() => buildToolCallArgsIndex(group.messages), [group.messages]);
  const navigate = useNavigate();
  const sectionRef = React.useRef<HTMLElement>(null);
  // Deep-linked turn: scroll into view and flash the header. Start yellow on
  // first paint (no fade-in), then drop the flag so `transition-colors` fades
  // it back to the normal header bg — a momentary flash rather than a
  // persistent ring (which read as janky against the sticky header).
  const [flashing, setFlashing] = React.useState(highlight);
  React.useEffect(() => {
    if (!highlight || !sectionRef.current) {
      return;
    }
    sectionRef.current.scrollIntoView({ block: 'center' });
    const timer = setTimeout(() => setFlashing(false), 700);
    return () => clearTimeout(timer);
  }, [highlight]);
  return (
    <section ref={sectionRef} className="border-border-primary border-t first:border-t-0">
      <header
        className={cx(
          'text-fg-tertiary sticky top-0 z-10 flex flex-wrap items-center gap-3 px-2 py-1 font-mono text-[11px] uppercase tracking-wide transition-colors duration-700',
          flashing ? 'bg-bg-warning-solid' : 'bg-bg-secondary'
        )}
      >
        <span className="text-fg-primary">{headerTimestamp}</span>
        <span>turn {group.turnId.slice(0, 8)}</span>
        <span>{group.messages.length} msg</span>
        <span>{group.roundCount} rounds</span>
        <span>{group.toolCallCount} tool calls</span>
        <span className="text-fg-primary normal-case">{formatDurationMs(group.durationMs)}</span>
        {feedback.length > 0 && (
          <Tooltip
            wide
            title="Feedback"
            description={
              <div className="flex flex-col gap-2 text-left">
                {feedback.map((fb) => (
                  <div key={fb.id} className="flex flex-col gap-0.5">
                    <div className="font-mono text-[10px] uppercase tracking-wide text-white/80">
                      {fb.submitterEmail ?? fb.userId} · {formatTimeAgo(fb.createdAt)}
                    </div>
                    <div className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">{fb.body}</div>
                  </div>
                ))}
              </div>
            }
          >
            <TooltipTrigger
              onPress={() => void navigate(assistantFeedbackAnchorPath(feedback[0].id))}
              className="border-border-brand text-fg-brand-primary hover:bg-bg-primary cursor-pointer border px-1 py-px font-mono text-[11px] normal-case"
            >
              💬 {feedback.length} feedback
            </TooltipTrigger>
          </Tooltip>
        )}
      </header>
      <div className="border-border-primary ml-1 border-l pl-2">
        {segments.map((segment, idx) => {
          if (segment.kind === 'single') {
            return (
              <MessageCard
                key={segment.message.id}
                message={segment.message}
                turnStartedAtMs={group.startedAtMs}
                toolCallArgs={toolCallArgs}
              />
            );
          }
          return (
            <div key={`parallel-${idx}`} className="border-fg-warning-primary/30 my-1 border-l-2 pl-2">
              {/*
                Right-padding math: per-row chips end at
                `outer-right - 1rem` because each MessageCard's
                article has `pr-2` (0.5rem) AND its inner button has
                `w-full -ml-1 px-1` which adds another 0.5rem of
                effective right inset (button-right = article-content-
                right - 0.25, button-content-right = -0.25 more). The
                parallel header has no article/button wrapper, so it
                must explicitly carry the full 1rem (`pr-4`) on its
                own to land chips in the same screen column as the
                rows below.
              */}
              <div className="flex items-center gap-2 px-1 py-0.5 pr-4 font-mono text-[10px] uppercase tracking-wide">
                <span className="text-fg-quaternary">⇄</span>
                <span className="text-fg-quaternary">{segment.messages.length} parallel</span>
                <ParallelMetadata
                  startedAtMs={segment.startedAtMs}
                  maxLatencyMs={segment.maxLatencyMs}
                  turnStartedAtMs={group.startedAtMs}
                />
              </div>
              {segment.messages.map((message) => (
                <MessageCard
                  key={message.id}
                  message={message}
                  turnStartedAtMs={group.startedAtMs}
                  toolCallArgs={toolCallArgs}
                />
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
};
